'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import {
  Archive,
  Briefcase,
  Camera,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  MapPin,
} from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

const MILESTONE_TKEYS: Record<
  string,
  { title: string; desc: string; recordType: string; context: string; preserves: string }
> = {
  Childhood: {
    title: 'memory_lab.milestone_childhood_title',
    desc: 'memory_lab.milestone_childhood_desc',
    recordType: 'memory_lab.milestone_childhood_record_type',
    context: 'memory_lab.milestone_childhood_context',
    preserves: 'memory_lab.milestone_childhood_preserves',
  },
  School: {
    title: 'memory_lab.milestone_academic_title',
    desc: 'memory_lab.milestone_academic_desc',
    recordType: 'memory_lab.milestone_academic_record_type',
    context: 'memory_lab.milestone_academic_context',
    preserves: 'memory_lab.milestone_academic_preserves',
  },
  Journey: {
    title: 'memory_lab.milestone_journey_title',
    desc: 'memory_lab.milestone_journey_desc',
    recordType: 'memory_lab.milestone_journey_record_type',
    context: 'memory_lab.milestone_journey_context',
    preserves: 'memory_lab.milestone_journey_preserves',
  },
  Career: {
    title: 'memory_lab.milestone_career_title',
    desc: 'memory_lab.milestone_career_desc',
    recordType: 'memory_lab.milestone_career_record_type',
    context: 'memory_lab.milestone_career_context',
    preserves: 'memory_lab.milestone_career_preserves',
  },
  Legacy: {
    title: 'memory_lab.milestone_legacy_title',
    desc: 'memory_lab.milestone_legacy_desc',
    recordType: 'memory_lab.milestone_legacy_record_type',
    context: 'memory_lab.milestone_legacy_context',
    preserves: 'memory_lab.milestone_legacy_preserves',
  },
};

export type Milestone = {
  year: string;
  title: string;
  description: string;
  suggestedRecordType?: string;
  credibilityLayer?: string;
  preserves?: string;
  weight?: number;
  category?: 'memory' | 'achievement' | 'journey' | 'career' | 'legacy';
};

export type MilestoneJourneyProps = {
  milestones?: Milestone[];
};

const DEFAULT_MILESTONES: Milestone[] = [
  {
    year: 'Childhood',
    title: 'Childhood memory',
    description:
      'Preserve early memories, family stories, places, photos, and objects that shaped who you are.',
    suggestedRecordType: 'Family memory',
    credibilityLayer: 'Private or family witness',
    preserves:
      'Nostalgia, early identity, people, places, and memories that often disappear with time.',
    weight: 1,
    category: 'memory',
  },
  {
    year: 'School',
    title: 'Academic chapter',
    description:
      'Save school years, first wins, certificates, friendships, teachers, projects, and formative learning moments.',
    suggestedRecordType: 'Academic story',
    credibilityLayer: 'Witness supported or institution verified',
    preserves: 'Education, growth, recognition, and the people who shaped the learning journey.',
    weight: 2,
    category: 'achievement',
  },
  {
    year: 'Journey',
    title: 'Places and experiences',
    description:
      'Record travel, migration, meaningful places, cultural memories, and experiences that changed your perspective.',
    suggestedRecordType: 'Journey record',
    credibilityLayer: 'Personal archive or witness supported',
    preserves: 'Maps, places, transitions, memories, and the emotional geography of a life.',
    weight: 3,
    category: 'journey',
  },
  {
    year: 'Career',
    title: 'Career landmark',
    description:
      'Preserve work milestones, projects, roles, launches, contributions, awards, and professional growth.',
    suggestedRecordType: 'Career achievement',
    credibilityLayer: 'Witness supported or institution verified',
    preserves: 'Effort, skill, credibility, and the story behind professional achievement.',
    weight: 4,
    category: 'career',
  },
  {
    year: 'Legacy',
    title: 'Living legacy',
    description:
      'Connect memories, achievements, witnesses, and verified records into a profile that can be shared or kept private.',
    suggestedRecordType: 'Living record',
    credibilityLayer: 'Public or private vault',
    preserves:
      "A fuller record of a person's life, identity, relationships, contributions, and proof.",
    weight: 5,
    category: 'legacy',
  },
];

const CATEGORY_STYLES = {
  memory: {
    accent: '#c98163',
    iconClass: 'text-[#d79b82]',
    dotColor: '#c98163',
    icon: Camera,
  },
  achievement: {
    accent: '#d2ad67',
    iconClass: 'text-[#dfc17e]',
    dotColor: '#d2ad67',
    icon: GraduationCap,
  },
  journey: {
    accent: '#a9b99b',
    iconClass: 'text-[#c5d0b9]',
    dotColor: '#a9b99b',
    icon: MapPin,
  },
  career: {
    accent: '#bc9655',
    iconClass: 'text-[#d6b270]',
    dotColor: '#bc9655',
    icon: Briefcase,
  },
  legacy: {
    accent: '#d7c18c',
    iconClass: 'text-[#e2d2aa]',
    dotColor: '#d7c18c',
    icon: Archive,
  },
} as const;

const FIGURE_FOOTPRINT = 30;

const ZONE_COPY: Record<string, { label: string; subtext: string }> = {
  Childhood: { label: 'Childhood', subtext: 'Roots & beginnings' },
  School: { label: 'School years', subtext: 'Learning & discovery' },
  Journey: { label: 'Places & experiences', subtext: 'Exploration & memories' },
  Career: { label: 'Career & milestones', subtext: 'Growth & achievement' },
  Legacy: { label: 'Legacy & beyond', subtext: 'Reflection & continuation' },
};

const DESKTOP_PATH_Y = [68, 58, 66, 54, 40];
const MOBILE_PATH_X = [38, 61, 39, 62, 48];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function categoryOf(milestone: Milestone) {
  return CATEGORY_STYLES[milestone.category ?? 'memory'] ?? CATEGORY_STYLES.memory;
}

function interpolate(values: number[], progress: number) {
  if (values.length === 0) return 50;
  const low = clamp(Math.floor(progress), 0, values.length - 1);
  const high = clamp(Math.ceil(progress), 0, values.length - 1);
  const amount = progress - low;
  return values[low] + (values[high] - values[low]) * amount;
}

function ScenicBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <Image
        src="/images/life-journey/life-journey-realistic-alberta.png"
        alt=""
        fill
        sizes="(min-width: 1024px) 70vw, 100vw"
        className="object-cover object-[48%_center] saturate-[0.72] contrast-[1.04] lg:object-center"
      />
      <div className="absolute inset-0 bg-[#061411]/45" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,18,15,0.72)_0%,rgba(5,18,15,0.16)_43%,rgba(5,18,15,0.38)_100%)] lg:bg-[linear-gradient(0deg,rgba(4,15,13,0.80)_0%,rgba(4,15,13,0.10)_54%,rgba(4,15,13,0.42)_100%)]" />
      <div className="absolute inset-0 border-y border-[#e8dcc3]/[0.16]" />
    </div>
  );
}

/**
 * Abstract walking silhouette. Head, torso, arms, and legs are separate SVG
 * elements animated with CSS keyframes (mj-anim-* in src/styles/tailwind.css).
 * No animation library — transform/opacity only.
 */
function WalkingFigure({
  direction,
  walking,
  justArrived,
  reducedMotion,
  accentColor,
  weight,
}: {
  direction: 'forward' | 'backward';
  walking: boolean;
  justArrived: boolean;
  reducedMotion: boolean;
  accentColor: string;
  weight: number;
}) {
  const showWalkAnim = walking && !reducedMotion;
  const showArrivalAnim = justArrived && !reducedMotion;
  const normalizedWeight = clamp(weight, 1, 5);
  const scale = 1 + (normalizedWeight - 1) * 0.014;

  const wrapperClass = showArrivalAnim ? 'mj-anim-arrival' : '';

  return (
    <svg
      viewBox="0 0 24 40"
      width={30}
      height={50}
      aria-hidden="true"
      focusable="false"
      className="pointer-events-none overflow-visible"
      style={{
        transform: `scaleX(${direction === 'backward' ? -1 : 1}) scale(${scale})`,
        transition: reducedMotion ? 'none' : 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1)',
      }}
    >
      <g className={wrapperClass}>
        <ellipse cx="12" cy="38.2" rx="4.6" ry="1.15" fill="#020c0b" opacity="0.4" />

        <line
          x1="12"
          y1="24"
          x2="9"
          y2="37"
          stroke="#182f28"
          strokeWidth="2.8"
          strokeLinecap="round"
          className={showWalkAnim ? 'mj-anim-leg-left' : ''}
        />
        <line
          x1="12"
          y1="24"
          x2="15"
          y2="37"
          stroke="#182f28"
          strokeWidth="2.8"
          strokeLinecap="round"
          className={showWalkAnim ? 'mj-anim-leg-right' : ''}
        />

        <g className={showWalkAnim ? 'mj-anim-torso' : ''}>
          <line
            x1="12"
            y1="12.5"
            x2="8.5"
            y2="21"
            stroke="#e4e8df"
            strokeWidth="2.2"
            strokeLinecap="round"
            className={showWalkAnim ? 'mj-anim-arm-left' : ''}
          />
          {/* Coat: flat matte fill, tinted faintly by the active chapter's accent */}
          <path
            d="M8.4 13.2c0-2.4 1.6-4.2 3.6-4.2s3.6 1.8 3.6 4.2v10c0 .9-.7 1.6-1.6 1.6H10c-.9 0-1.6-.7-1.6-1.6Z"
            fill="#e4e8df"
          />
          <path
            d="M8.4 13.2c0-2.4 1.6-4.2 3.6-4.2s3.6 1.8 3.6 4.2v10c0 .9-.7 1.6-1.6 1.6H10c-.9 0-1.6-.7-1.6-1.6Z"
            fill={accentColor}
            opacity="0.16"
          />
          <line
            x1="12"
            y1="12.5"
            x2="15.5"
            y2="21"
            stroke="#e4e8df"
            strokeWidth="2.2"
            strokeLinecap="round"
            className={showWalkAnim ? 'mj-anim-arm-right' : ''}
          />
          {/* Satchel: the one accent-colored mark, tying the figure to the active pillar color */}
          <rect
            x="13.6"
            y="14.6"
            width="3.6"
            height="4.4"
            rx="0.9"
            fill={accentColor}
            opacity="0.86"
          />
          <path
            d="M15.4 14.6v-1.6"
            stroke={accentColor}
            strokeWidth="1"
            strokeLinecap="round"
            opacity="0.7"
          />
          <circle cx="12" cy="6.4" r="3.7" fill="#e4e8df" />
        </g>
      </g>
    </svg>
  );
}

export default function MilestoneJourney({
  milestones = DEFAULT_MILESTONES,
}: MilestoneJourneyProps) {
  const { t } = useTranslation();
  const journeyMilestones = milestones?.length ? milestones : DEFAULT_MILESTONES;
  const count = journeyMilestones.length;
  const maxProgress = Math.max(0, count - 1);

  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [walking, setWalking] = useState(false);
  const [justArrived, setJustArrived] = useState(false);
  const [strideDuration, setStrideDuration] = useState(0.6);
  const [isVertical, setIsVertical] = useState(true);
  const [trackLength, setTrackLength] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  const activeIndex = clamp(Math.round(progress), 0, maxProgress);
  const activeMilestone = journeyMilestones[activeIndex];
  const activeCategory = categoryOf(activeMilestone);

  const translatedMilestoneCopy = (
    milestone: Milestone,
    field: keyof (typeof MILESTONE_TKEYS)[string],
    fallback: string
  ) => {
    const key = MILESTONE_TKEYS[milestone.year]?.[field];
    return key ? t(key as any) : fallback;
  };

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const progressRef = useRef(0);
  const tweenRaf = useRef<number | null>(null);
  const arrivalTimer = useRef<number | null>(null);
  const scrollSettleTimer = useRef<number | null>(null);
  const manualControlUntilRef = useRef(0);
  const draggingRef = useRef(false);
  const dragState = useRef({ startClient: 0, startProgress: 0, lastClient: 0, lastTime: 0 });

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  // Reduced motion detection.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Desktop vs mobile path orientation. Defaults to vertical (mobile-first,
  // hydration-safe) and corrects after mount — matches this file's existing
  // lg: breakpoint convention.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsVertical(!mq.matches);
    const onChange = (event: MediaQueryListEvent) => setIsVertical(!event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Measure the track's usable length (width when horizontal, height when vertical).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = trackRef.current;
    if (!node) return;
    const measure = () => {
      setTrackLength(isVertical ? node.offsetHeight : node.offsetWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isVertical]);

  const cancelTween = useCallback(() => {
    if (tweenRaf.current !== null) {
      cancelAnimationFrame(tweenRaf.current);
      tweenRaf.current = null;
    }
  }, []);

  const triggerArrival = useCallback(() => {
    setJustArrived(true);
    if (arrivalTimer.current !== null) window.clearTimeout(arrivalTimer.current);
    arrivalTimer.current = window.setTimeout(() => setJustArrived(false), 650);
  }, []);

  const animateProgressTo = useCallback(
    (target: number, options?: { duration?: number }) => {
      const clampedTarget = clamp(target, 0, maxProgress);
      manualControlUntilRef.current = performance.now() + 2500;
      cancelTween();
      const start = progressRef.current;
      setDirection(clampedTarget >= start ? 'forward' : 'backward');
      setStrideDuration(0.6);

      if (reducedMotion) {
        setProgress(clampedTarget);
        progressRef.current = clampedTarget;
        setWalking(false);
        triggerArrival();
        return;
      }

      const distance = Math.abs(clampedTarget - start);
      const duration = options?.duration ?? clamp(380 + distance * 260, 260, 900);
      const startTime = performance.now();
      setWalking(true);

      const step = (now: number) => {
        const t = clamp((now - startTime) / duration, 0, 1);
        const eased = easeOutCubic(t);
        const value = start + (clampedTarget - start) * eased;
        progressRef.current = value;
        setProgress(value);
        if (t < 1) {
          tweenRaf.current = requestAnimationFrame(step);
        } else {
          progressRef.current = clampedTarget;
          setProgress(clampedTarget);
          setWalking(false);
          tweenRaf.current = null;
          triggerArrival();
        }
      };
      tweenRaf.current = requestAnimationFrame(step);
    },
    [cancelTween, maxProgress, reducedMotion, triggerArrival]
  );

  // Scroll-linked movement: only listens while the section is near the
  // viewport, throttled to one rAF-scheduled update at a time.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (reducedMotion) {
      setWalking(false);
      return;
    }
    const section = sectionRef.current;
    if (!section) return;

    let active = false;
    let ticking = false;

    const applyScrollProgress = () => {
      if (draggingRef.current) return;
      if (performance.now() < manualControlUntilRef.current) return;
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const start = viewportHeight;
      const end = -rect.height;
      const span = start - end || 1;
      const raw = (start - rect.top) / span;
      const fraction = clamp(raw, 0, 1);
      const target = fraction * maxProgress;
      const prev = progressRef.current;
      if (Math.abs(target - prev) < 0.001) return;

      cancelTween();
      setDirection(target >= prev ? 'forward' : 'backward');
      setWalking(true);
      progressRef.current = target;
      setProgress(target);

      if (scrollSettleTimer.current !== null) window.clearTimeout(scrollSettleTimer.current);
      scrollSettleTimer.current = window.setTimeout(() => {
        setWalking(false);
        const nearest = Math.round(progressRef.current);
        if (Math.abs(progressRef.current - nearest) < 0.03) triggerArrival();
      }, 140);
    };

    const onScroll = () => {
      if (!active || ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        applyScrollProgress();
        ticking = false;
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        active = entries[0]?.isIntersecting ?? false;
      },
      { rootMargin: '20% 0px 20% 0px', threshold: 0 }
    );
    io.observe(section);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (scrollSettleTimer.current !== null) window.clearTimeout(scrollSettleTimer.current);
    };
  }, [cancelTween, maxProgress, reducedMotion, triggerArrival]);

  useEffect(
    () => () => {
      cancelTween();
      if (arrivalTimer.current !== null) window.clearTimeout(arrivalTimer.current);
    },
    [cancelTween]
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Let touch users scroll the page through the tall mobile scene. A dedicated
    // horizontal chapter scrubber below retains direct drag control on mobile.
    if (isVertical && event.pointerType !== 'mouse') return;
    cancelTween();
    event.currentTarget.setPointerCapture(event.pointerId);
    const client = isVertical ? event.clientY : event.clientX;
    dragState.current = {
      startClient: client,
      startProgress: progressRef.current,
      lastClient: client,
      lastTime: performance.now(),
    };
    draggingRef.current = true;
    setWalking(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    event.preventDefault();
    const client = isVertical ? event.clientY : event.clientX;
    const usable = Math.max(1, trackLength - FIGURE_FOOTPRINT);
    const deltaPx = client - dragState.current.startClient;
    const deltaProgress = (deltaPx / usable) * maxProgress;
    const next = clamp(dragState.current.startProgress + deltaProgress, 0, maxProgress);

    if (!reducedMotion) {
      const now = performance.now();
      const dt = Math.max(1, now - dragState.current.lastTime);
      const instantVelocity = Math.abs(client - dragState.current.lastClient) / dt;
      const speedFactor = clamp(instantVelocity * 6, 0.35, 2.4);
      setStrideDuration(clamp(0.62 / speedFactor, 0.2, 0.9));
      dragState.current.lastClient = client;
      dragState.current.lastTime = now;
    }

    setDirection(next >= progressRef.current ? 'forward' : 'backward');
    progressRef.current = next;
    setProgress(next);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    animateProgressTo(Math.round(progressRef.current), { duration: 240 });
  };

  const handleTrackKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const stepKeys: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    };
    if (event.key in stepKeys) {
      event.preventDefault();
      animateProgressTo(activeIndex + stepKeys[event.key]);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      animateProgressTo(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      animateProgressTo(maxProgress);
    }
  };

  const fraction = maxProgress === 0 ? 0 : clamp(progress / maxProgress, 0, 1);
  const desktopPathProgress = fraction * (DESKTOP_PATH_Y.length - 1);
  const mobilePathProgress = fraction * (MOBILE_PATH_X.length - 1);
  const desktopFigureX = 2 + fraction * 96;
  const desktopFigureY = interpolate(DESKTOP_PATH_Y, desktopPathProgress);
  const mobileFigureX = interpolate(MOBILE_PATH_X, mobilePathProgress);
  const mobileFigureY = 5 + fraction * 90;
  const hasActiveDetails = Boolean(
    activeMilestone.suggestedRecordType ||
    activeMilestone.credibilityLayer ||
    activeMilestone.preserves
  );

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-w-0 max-w-full border-t border-[#e8dcc3]/[0.16] pt-8 sm:pt-10"
      aria-label={t('memory_lab.life_journey_title' as any)}
    >
      <header className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-7 lg:grid-cols-[minmax(0,0.72fr)_minmax(18rem,0.28fr)] lg:items-end">
        <div className="min-w-0 max-w-4xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#c9a968]">
            01 — A life, chapter by chapter
          </p>
          <h2
            className="mt-4 break-words text-balance text-4xl leading-[1.02] text-[#f4efe4] sm:text-5xl lg:text-[3.65rem]"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            {t('memory_lab.life_journey_title' as any)}
          </h2>
          <p className="mt-5 max-w-2xl break-words text-base leading-7 text-[#b9c3b9] sm:text-lg sm:leading-8">
            {t('memory_lab.life_journey_subtitle' as any)}
          </p>
        </div>
        <div className="flex items-center gap-3 lg:justify-end">
          <span className="mr-1 hidden font-mono text-[11px] uppercase tracking-[0.12em] text-[#899a90] sm:inline">
            Chapter {String(activeIndex + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
          </span>
          <button
            type="button"
            aria-label={t('memory_lab.previous' as any)}
            onClick={() => animateProgressTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e8dcc3]/[0.24] text-[#e8dcc3] transition-[transform,border-color,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] [@media(hover:hover)_and_(pointer:fine)]:hover:border-[#c9a968] [@media(hover:hover)_and_(pointer:fine)]:hover:text-[#e2c98f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a968] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1614] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none motion-reduce:active:scale-100"
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('common.next' as any)}
            onClick={() => animateProgressTo(activeIndex + 1)}
            disabled={activeIndex === maxProgress}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e8dcc3]/[0.24] text-[#e8dcc3] transition-[transform,border-color,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] [@media(hover:hover)_and_(pointer:fine)]:hover:border-[#c9a968] [@media(hover:hover)_and_(pointer:fine)]:hover:text-[#e2c98f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a968] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1614] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none motion-reduce:active:scale-100"
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="mt-10 grid min-w-0 grid-cols-[minmax(0,1fr)] border-y border-[#e8dcc3]/[0.16] lg:grid-cols-[minmax(0,1.68fr)_minmax(20rem,0.62fr)] lg:items-stretch">
        {/* Scenic journey pane */}
        <div className="relative h-[680px] w-full min-w-0 max-w-full overflow-hidden bg-[#071a19] sm:h-[720px] lg:h-[520px]">
          <ScenicBackdrop />

          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-4 px-5 pt-5 sm:px-7 sm:pt-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#e8dcc3]/75 sm:text-[11px]">
              <span className="lg:hidden">Scroll freely &middot; use the scrubber below</span>
              <span className="hidden lg:inline">{t('memory_lab.drag_hint' as any)}</span>
            </p>
            <span className="font-mono text-[11px] tracking-[0.12em] text-[#f4efe4] sm:hidden">
              {String(activeIndex + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
            </span>
          </div>

          <div className="absolute inset-0 z-10">
            {/* Full-size hit area: the visual line stays thin, but drag/keyboard
                target the whole pane for a comfortable touch/pointer target. */}
            <div
              ref={trackRef}
              role="slider"
              tabIndex={0}
              aria-label={t('memory_lab.life_journey_title' as any)}
              aria-valuemin={0}
              aria-valuemax={maxProgress}
              aria-valuenow={activeIndex}
              aria-valuetext={`${activeMilestone.year}, ${translatedMilestoneCopy(activeMilestone, 'title', activeMilestone.title)}`}
              onKeyDown={handleTrackKeyDown}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              className="absolute inset-x-3 bottom-[6.5rem] top-8 cursor-default touch-pan-y select-none outline-none focus-visible:ring-2 focus-visible:ring-[#c9a968] focus-visible:ring-offset-2 focus-visible:ring-offset-[#071a19] sm:inset-x-5 lg:bottom-8 lg:top-8 lg:cursor-grab lg:touch-none lg:active:cursor-grabbing"
            >
              <svg
                className="pointer-events-none absolute inset-0 hidden h-full w-full overflow-visible lg:block"
                viewBox="0 0 1000 400"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M20 272 C120 272 170 224 260 232 S410 286 500 264 S650 198 740 216 S900 185 980 160"
                  fill="none"
                  stroke="#06110e"
                  strokeOpacity=".82"
                  strokeWidth="21"
                  strokeLinecap="round"
                />
                <path
                  d="M20 272 C120 272 170 224 260 232 S410 286 500 264 S650 198 740 216 S900 185 980 160"
                  fill="none"
                  stroke="#eee2c9"
                  strokeOpacity=".30"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray="1 3.4"
                />
                <path
                  d="M20 272 C120 272 170 224 260 232 S410 286 500 264 S650 198 740 216 S900 185 980 160"
                  fill="none"
                  pathLength="1"
                  stroke="#c9a968"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="1"
                  strokeDashoffset={1 - fraction}
                  className={justArrived && !reducedMotion ? 'mj-trail-flash' : ''}
                />
              </svg>
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full overflow-visible lg:hidden"
                viewBox="0 0 320 700"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M122 35 C122 105 198 118 195 193 S122 278 125 350 S200 429 198 508 S153 594 154 665"
                  fill="none"
                  stroke="#06110e"
                  strokeOpacity=".84"
                  strokeWidth="19"
                  strokeLinecap="round"
                />
                <path
                  d="M122 35 C122 105 198 118 195 193 S122 278 125 350 S200 429 198 508 S153 594 154 665"
                  fill="none"
                  stroke="#eee2c9"
                  strokeOpacity=".30"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray="1 3.4"
                />
                <path
                  d="M122 35 C122 105 198 118 195 193 S122 278 125 350 S200 429 198 508 S153 594 154 665"
                  fill="none"
                  pathLength="1"
                  stroke="#c9a968"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="1"
                  strokeDashoffset={1 - fraction}
                  className={justArrived && !reducedMotion ? 'mj-trail-flash' : ''}
                />
              </svg>

              {/* Milestone markers */}
              {journeyMilestones.map((item, index) => {
                const cat = categoryOf(item);
                const fractionAtIndex = maxProgress === 0 ? 0 : index / maxProgress;
                const desktopX = 2 + fractionAtIndex * 96;
                const desktopY = interpolate(
                  DESKTOP_PATH_Y,
                  fractionAtIndex * (DESKTOP_PATH_Y.length - 1)
                );
                const mobileX = interpolate(
                  MOBILE_PATH_X,
                  fractionAtIndex * (MOBILE_PATH_X.length - 1)
                );
                const mobileY = 5 + fractionAtIndex * 90;
                const state =
                  index < activeIndex ? 'passed' : index === activeIndex ? 'active' : 'upcoming';
                const zoneCopy = ZONE_COPY[item.year] ?? { label: item.year, subtext: item.title };
                return (
                  <div
                    key={`${item.year}-${item.title}-${index}`}
                    style={
                      {
                        '--mj-mobile-x': `${mobileX}%`,
                        '--mj-mobile-y': `${mobileY}%`,
                        '--mj-desktop-x': `${desktopX}%`,
                        '--mj-desktop-y': `${desktopY}%`,
                      } as React.CSSProperties
                    }
                    className="pointer-events-none absolute left-[var(--mj-mobile-x)] top-[var(--mj-mobile-y)] -translate-x-1/2 -translate-y-1/2 lg:left-[var(--mj-desktop-x)] lg:top-[var(--mj-desktop-y)]"
                  >
                    <button
                      type="button"
                      aria-label={`${t('memory_lab.go_to_milestone' as any)} ${zoneCopy.label}: ${translatedMilestoneCopy(item, 'title', item.title)}`}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        animateProgressTo(index);
                      }}
                      className={`pointer-events-auto relative flex h-11 w-11 items-center justify-center rounded-full transition-[transform,opacity] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a968] focus-visible:ring-offset-2 focus-visible:ring-offset-[#071a19] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${state === 'active' ? 'scale-110' : 'scale-100'} ${state === 'upcoming' ? 'opacity-65' : 'opacity-100'}`}
                    >
                      <span
                        className={`absolute h-8 w-8 rounded-full border-[1.5px] ${state === 'active' ? 'border-[#eee2c9]/65 bg-[#071a19]/90' : 'border-[#eee2c9]/24 bg-[#071a19]/70'}`}
                      />
                      <span
                        className={`relative h-3 w-3 rounded-full border border-[#020c0b]/40 ${state === 'active' && justArrived && !reducedMotion ? 'mj-marker-active-settle' : ''}`}
                        style={{
                          background:
                            state === 'upcoming' ? 'rgba(215,255,242,0.34)' : cat.dotColor,
                        }}
                      />
                    </button>
                    <div
                      className={`absolute top-10 w-36 border-l pl-2.5 text-left lg:top-9 lg:w-40 ${
                        index === 0
                          ? 'left-0'
                          : index === count - 1
                            ? 'right-0'
                            : 'left-1/2 -translate-x-1/2'
                      } ${state === 'active' ? 'opacity-100' : 'opacity-70'}`}
                      style={{
                        borderColor: state === 'active' ? cat.accent : 'rgba(238,226,201,.22)',
                      }}
                    >
                      <p className="text-[11px] font-bold leading-4 text-[#f4efe4] [text-shadow:0_1px_8px_rgba(0,0,0,0.92)]">
                        {zoneCopy.label}
                      </p>
                      <p className="mt-0.5 text-[10px] leading-4 text-[#c6cec4] [text-shadow:0_1px_8px_rgba(0,0,0,0.92)]">
                        {zoneCopy.subtext}
                      </p>
                    </div>
                  </div>
                );
              })}

              {/* Walking figure follows the active point along the journey. */}
              <div
                className="pointer-events-none absolute left-[var(--mj-mobile-x)] top-[var(--mj-mobile-y)] z-20 lg:left-[var(--mj-desktop-x)] lg:top-[var(--mj-desktop-y)]"
                style={
                  {
                    '--mj-mobile-x': `${mobileFigureX}%`,
                    '--mj-mobile-y': `${mobileFigureY}%`,
                    '--mj-desktop-x': `${desktopFigureX}%`,
                    '--mj-desktop-y': `${desktopFigureY}%`,
                    transform: 'translate(-50%, calc(-100% + 5px))',
                  } as React.CSSProperties
                }
              >
                <div style={{ ['--mj-stride-duration' as string]: `${strideDuration}s` }}>
                  <WalkingFigure
                    direction={direction}
                    walking={walking}
                    justArrived={justArrived}
                    reducedMotion={reducedMotion}
                    accentColor={activeCategory.accent}
                    weight={activeMilestone.weight ?? 1}
                  />
                </div>
              </div>
            </div>

            <div className="absolute inset-x-5 bottom-5 z-30 border-t border-[#eee2c9]/25 pt-4 lg:hidden">
              <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-[#e8dcc3]/75">
                <span>Drag chapters</span>
                <span>{ZONE_COPY[activeMilestone.year]?.label ?? activeMilestone.year}</span>
              </div>
              <input
                type="range"
                min={0}
                max={maxProgress}
                step={1}
                value={activeIndex}
                disabled={maxProgress === 0}
                aria-label={`${t('memory_lab.life_journey_title' as any)} chapter scrubber`}
                onPointerDown={(event) => event.stopPropagation()}
                onChange={(event) =>
                  animateProgressTo(Number(event.currentTarget.value), { duration: 220 })
                }
                className="h-8 w-full cursor-ew-resize touch-pan-x accent-[#c9a968] disabled:cursor-not-allowed disabled:opacity-40"
              />
            </div>
          </div>
        </div>

        {/* Active milestone content */}
        <aside
          aria-live="polite"
          className="relative w-full min-w-0 max-w-full border-t border-[#e8dcc3]/[0.16] bg-[#0a1714] px-5 py-8 sm:px-8 sm:py-10 lg:border-l lg:border-t-0 lg:px-9 lg:py-12"
        >
          <div
            key={`${activeMilestone.year}-${activeMilestone.title}`}
            className="relative motion-safe:animate-fade-in"
          >
            <div className="flex items-center justify-between gap-4">
              <span
                className={`flex h-10 w-10 items-center justify-center border-l border-current ${activeCategory.iconClass}`}
              >
                <activeCategory.icon size={19} aria-hidden="true" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#a8b2a9]">
                {activeMilestone.year}
              </span>
            </div>
            <h3
              className="mt-8 break-words text-4xl leading-[1.05] text-[#f4efe4] lg:text-[2.8rem]"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {translatedMilestoneCopy(activeMilestone, 'title', activeMilestone.title)}
            </h3>
            <p className="mt-5 break-words text-base leading-8 text-[#b8c1b8]">
              {translatedMilestoneCopy(activeMilestone, 'desc', activeMilestone.description)}
            </p>
            {hasActiveDetails && (
              <dl className="mt-9 min-w-0 divide-y divide-[#e8dcc3]/[0.12] border-y border-[#e8dcc3]/[0.16]">
                {activeMilestone.suggestedRecordType && (
                  <div className="min-w-0 py-4">
                    <dt className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[#899a90]">
                      {t('memory_lab.record_type' as any)}
                    </dt>
                    <dd className="mt-2 break-words text-sm font-medium leading-6 text-[#eee7d9]">
                      {translatedMilestoneCopy(
                        activeMilestone,
                        'recordType',
                        activeMilestone.suggestedRecordType
                      )}
                    </dd>
                  </div>
                )}
                {activeMilestone.credibilityLayer && (
                  <div className="min-w-0 py-4">
                    <dt className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[#899a90]">
                      {t('memory_lab.context_layer' as any)}
                    </dt>
                    <dd className="mt-2 break-words text-sm font-medium leading-6 text-[#eee7d9]">
                      {translatedMilestoneCopy(
                        activeMilestone,
                        'context',
                        activeMilestone.credibilityLayer
                      )}
                    </dd>
                  </div>
                )}
                {activeMilestone.preserves && (
                  <div className="min-w-0 py-4">
                    <dt className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[#899a90]">
                      {t('memory_lab.what_this_preserves' as any)}
                    </dt>
                    <dd className="mt-2 break-words text-sm font-medium leading-6 text-[#eee7d9]">
                      {translatedMilestoneCopy(
                        activeMilestone,
                        'preserves',
                        activeMilestone.preserves
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
